/**
 * Agents resource — admin management of app-managed agents (ARCHITECTURE §4).
 */

import type { HttpClient } from '../httpClient';
import { endpoints } from '../endpoints';
import type { Agent, NotificationPrefs, Role } from '../../types';

export interface CreateAgentBody {
  email: string;
  name: string;
  role: Role;
}

/**
 * Response of create / re-invite. Extends {@link Agent} with the set-password
 * `inviteLink`, which the backend only populates under the dev `console` email
 * provider (it is `null`/absent when a real mailer sends the invite). The UI
 * surfaces it as a copyable link when present so local flows are testable.
 */
export interface AgentCreated extends Agent {
  inviteLink?: string | null;
}

export class AgentsResource {
  constructor(private readonly http: HttpClient) {}

  list(opts?: { includeInactive?: boolean }): Promise<Agent[]> {
    return this.http.get(endpoints.agents.list(opts?.includeInactive));
  }

  /** Creating an agent triggers an invite email with a set-password token. */
  create(body: CreateAgentBody): Promise<AgentCreated> {
    return this.http.post(endpoints.agents.create(), body);
  }

  update(agentId: string, patch: Partial<Pick<Agent, 'name' | 'role'>>): Promise<Agent> {
    return this.http.patch(endpoints.agents.detail(agentId), patch);
  }

  /** Soft-delete (sets `deletedAt`); returns the deleted row. */
  remove(agentId: string): Promise<Agent> {
    return this.http.delete(endpoints.agents.detail(agentId));
  }

  /** Re-send the invite / set-password email; returns the agent + fresh `inviteLink`. */
  resendInvite(agentId: string): Promise<AgentCreated> {
    return this.http.post(endpoints.agents.invite(agentId));
  }

  /** Reactivate a deactivated agent (clears `deletedAt`; re-checks the plan cap). */
  reactivate(agentId: string): Promise<Agent> {
    return this.http.post(endpoints.agents.reactivate(agentId));
  }

  // ── Self: notification preferences ("My Notifications") ────────────────────
  /** The calling agent's saved notification preferences (agent-kind principals only). */
  notificationPrefs(): Promise<NotificationPrefs> {
    return this.http.get(endpoints.agents.notificationPrefs());
  }

  /** Shallow-merge a partial notification-preference patch for the calling agent. */
  updateNotificationPrefs(patch: NotificationPrefs): Promise<NotificationPrefs> {
    return this.http.patch(endpoints.agents.notificationPrefs(), patch);
  }
}
